import type {
  CategoryCreateInput,
  CategoryUpdateInput,
} from "@/modules/categories/domain/contracts";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { z } from "zod";
import type { CategoryDTO } from "../api/client";

const CATEGORY_PAGE_SIZE = 20;

const CategoryFormSchema = z.object({
  nameLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  slug: z.string().min(1, "ຕ້ອງໃສ່ Slug"),
  parentId: z.string().nullable().optional(),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type CategoryFormValues = z.output<typeof CategoryFormSchema>;

const DEV_FILL: CategoryFormValues = {
  nameLo: "ເສື້ອຜ້າ",
  nameTh: "เสื้อผ้า",
  nameEn: "Clothing",
  slug: "clothing",
  parentId: null,
  sortOrder: 0,
};

type CategoryFormProps = {
  categories: CategoryDTO[];
  initialValues?: Partial<CategoryFormValues>;
  onSubmit: (values: CategoryCreateInput | CategoryUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function CategoryForm({
  categories,
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: CategoryFormProps) {
  const methods = RHF.useForm<CategoryFormValues>({
    resolver: zodResolver(CategoryFormSchema) as Resolver<CategoryFormValues>,
    defaultValues: {
      nameLo: "",
      nameTh: "",
      nameEn: "",
      slug: "",
      parentId: null,
      sortOrder: 0,
      ...initialValues,
    },
  });

  const fillDev = () => {
    methods.reset(DEV_FILL);
  };

  // Auto-generate slug from English name
  const generateSlug = useCallback((text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/[\s_-]+/g, "-") // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  }, []);

  // Watch for changes in nameEn to auto-generate slug
  const nameEn = methods.watch("nameEn");
  useEffect(() => {
    if (nameEn && !initialValues?.slug) {
      const slug = generateSlug(nameEn);
      if (slug !== methods.getValues("slug")) {
        methods.setValue("slug", slug);
      }
    }
  }, [nameEn, generateSlug, methods, initialValues?.slug]);

  const categoryQueryFn = useCallback(
    async ({
      search,
      pageParam = 1,
    }: {
      search: string;
      pageParam: number;
    }) => {
      const s = search.trim().toLowerCase();
      const filtered = s
        ? categories.filter((c) => {
            const slug = c.slug.toLowerCase();
            const name =
              (c.name as { lo?: string; th?: string; en?: string })?.lo ??
              (c.name as { en?: string })?.en ??
              "";
            return slug.includes(s) || name.toLowerCase().includes(s);
          })
        : [...categories];
      const page = pageParam > 0 ? pageParam : 1;
      const start = (page - 1) * CATEGORY_PAGE_SIZE;
      const items = filtered.slice(start, start + CATEGORY_PAGE_SIZE);
      const nextPage =
        start + CATEGORY_PAGE_SIZE < filtered.length ? page + 1 : null;
      return { items, nextPage };
    },
    [categories],
  );

  const categoryPreloadFn = useCallback(
    (id: string) => {
      const c = categories.find((x) => x.id === id);
      return Promise.resolve(c ?? null);
    },
    [categories],
  );

  return (
    <FormRoot<CategoryFormValues>
      methods={methods as UseFormReturn<CategoryFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
          slug: vals.slug,
          parentId: vals.parentId ?? undefined,
          sortOrder: vals.sortOrder,
        })
      }
    >
      <div className="space-y-4" data-tourid="catalog-category-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຂໍ້ມູນຫມວດມູ່</h3>
          {onDevFill && (
            <Button type="button" variant="outline" size="sm" onClick={fillDev}>
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <div data-tourid="category-form-names">
          <FormInput
            name="nameLo"
            label="ຊື່ (ລາວ)"
            placeholder="ພາສາລາວ"
          />
          <FormInput name="nameTh" label="ຊື່ (ໄທ)" placeholder="ภาษาไทย" className="mt-4" />
          <FormInput name="nameEn" label="ຊື່ (EN)" placeholder="English" className="mt-4" />
        </div>
        <FormInput
          name="slug"
          label="Slug"
          placeholder="slug"
          data-tourid="category-form-slug"
          readOnly
          hint="ລະຫັດອ້າງອີງ URL (ສ້າງຈາກຊື່ພາສາອັງກິດໂດຍອັດຕະໂນມັດ)"
        />
        <div data-tourid="category-form-parent">
          <label className="mb-1 block font-medium text-sm">ຫມວດມູ່ແມ່</label>
          <RHF.Controller
            name="parentId"
            control={methods.control}
            render={({ field }) => (
              <InfiniteCombobox<CategoryDTO>
                queryKey={["catalog", "categories"]}
                queryFn={categoryQueryFn}
                preloadQueryFn={categoryPreloadFn}
                getLabel={(item) =>
                  (item.name as { lo?: string })?.lo ??
                  (item.name as { en?: string })?.en ??
                  item.slug
                }
                getValue={(item) => item.id}
                value={(field.value as string | null) ?? ""}
                onValueChange={(v) => field.onChange(v === "" ? null : v)}
                placeholder="— ບໍ່ເລືອກ"
                clearable
              />
            )}
          />
        </div>
        <FormInput
          name="sortOrder"
          label="ລຳດັບ"
          type="number"
          min={0}
          data-tourid="category-form-order"
        />
        <div className="flex gap-2 pt-2" data-tourid="category-form-submit">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
